import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import arrowIcon from "@assets/images/Frame-9.svg";

/* ── Types ── */
interface CookiePreferences {
  functional: boolean;
  performance: boolean;
  targeting: boolean;
}

interface CookieModalProps {
  onClose: () => void;
}

/* ── Toggle ── */
function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-[48px] h-[26px] rounded-full transition-colors duration-300 flex-shrink-0 ${
        checked ? "bg-[#F03813]" : "bg-[#D1D5DB]"
      }`}
    >
      <span
        className={`absolute top-[3px] left-[3px] w-[20px] h-[20px] rounded-full bg-white shadow transition-transform duration-300 ${
          checked ? "translate-x-[22px]" : "translate-x-0"
        }`}
      />
    </button>
  );
}

/* ── Cookie Row ── */
interface CookieRowProps {
  number: string;
  title: string;
  description: string;
  alwaysActive?: boolean;
  checked?: boolean;
  onToggle?: (v: boolean) => void;
  isOpen: boolean;
  onExpand: () => void;
}

function CookieRow({
  number,
  title,
  description,
  alwaysActive,
  checked,
  onToggle,
  isOpen,
  onExpand,
}: CookieRowProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div className="bg-white p-4 border mt-3 border-[#62636C33] rounded-2xl last:border-b-0">
      <div
        className="flex items-center gap-3 py-3 cursor-pointer"
        onClick={onExpand}
      >
        {/* Number badge */}
        <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#F24D2B] text-white text-[13px] font-semibold flex items-center justify-center">
          {number}
        </span>

        {/* Title */}
        <p className="flex-1 text-[14px] md:text-[16px] font-medium text-[#0A0E2E] min-w-0">
          {title}
        </p>

        {/* Right side */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {alwaysActive ? (
            <span className="text-[#F03813] text-[12px] md:text-[14px] font-medium whitespace-nowrap">
              Always Active
            </span>
          ) : (
            <div onClick={(e) => e.stopPropagation()}>
              <Toggle checked={checked!} onChange={onToggle!} />
            </div>
          )}

          {/* Expand/collapse */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onExpand();
            }}
            className="w-6 h-6 flex items-center justify-center text-[#F03813] text-[20px] font-light leading-none flex-shrink-0"
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? "—" : "+"}
          </button>
        </div>
      </div>

      {/* Description — real height animation */}
      <div
        style={{ height }}
        className="overflow-hidden transition-[height] duration-500 ease-in-out"
      >
        <div ref={contentRef} className="pb-3 pl-12 pr-2">
          <div
            className={`border-t border-[#E5E7EB] mb-3 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
          />
          <p className="text-[13px] md:text-[14px] text-[#62636C] leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Banner ── */
function CookieBanner({
  onManage,
  onRejectAll,
  onAcceptAll,
}: {
  onManage: () => void;
  onRejectAll: () => void;
  onAcceptAll: () => void;
}) {
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:left-auto lg:right-6 lg:max-w-[520px] z-[1200] bg-white rounded-[20px] shadow-[0_8px_40px_rgba(0,0,0,0.18)] p-5 md:p-6">
      <h3 className="text-[20px] md:text-[22px] font-semibold text-[#62636C] mb-2">
        Cookies
      </h3>
      <p className="text-[14px] md:text-[15px] text-[#62636C] leading-relaxed mb-5">
        We use cookies to give you the best possible online experience, ensuring
        our websites are reliable and secure. Cookies may be used by our
        advertising partners to build a profile of your interests and will be
        used for personalization of adverts on third party websites. You can
        allow all cookies or manage what we use them for. You can find out more
        on our{" "}
        <a href="/privacy-policy" className="text-[#F03813] underline">
          privacy policy page
        </a>
        .
      </p>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onManage}
          className="text-[14px] md:text-[15px] text-[#F03813] font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
        >
          Manage Cookies
        </button>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={onRejectAll}
            className="px-4 py-2 md:py-2.5 rounded-full border border-[#0A0E2E] text-[#0A0E2E] text-[13px] md:text-[14px] font-medium hover:bg-[#F5F5F5] transition-colors"
          >
            Reject All
          </button>
          <button
            type="button"
            onClick={onAcceptAll}
            className="px-4 py-2 md:py-2.5 rounded-full bg-[#080c2d] text-white text-[13px] md:text-[14px] font-medium flex items-center gap-2 hover:bg-[#0f1554] transition-colors group/btn"
          >
            Accept All
            <img
              src={arrowIcon}
              alt=""
              className="w-6 h-6 transition-transform duration-300 group-hover/btn:translate-x-0.5"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Preference Modal ── */
function CookiePreferenceModal({
  preferences,
  onPreferencesChange,
  onRejectAll,
  onConfirm,
  onAllowAll,
  onClose,
}: {
  preferences: CookiePreferences;
  onPreferencesChange: (prefs: CookiePreferences) => void;
  onRejectAll: () => void;
  onConfirm: () => void;
  onAllowAll: () => void;
  onClose: () => void;
}) {
  const [visible, setVisible] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);

  const handleExpand = (id: string) =>
    setOpenRow((prev) => (prev === id ? null : id));

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-[1368px] bg-[#F1F1F1] rounded-[20px] md:rounded-[24px] shadow-[0_25px_50px_rgba(0,0,0,0.25)] max-h-[90vh] overflow-y-auto transition-all duration-300 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="p-6 md:py-[64px] md:px-[120px]">
          <h2 className="text-[24px] md:text-[28px] font-semibold text-[#62636C] mb-3">
            Cookie Preference
          </h2>

          <p className="text-[14px] md:text-[15px] text-[#62636C] leading-relaxed mb-6">
            When you visit any website, it may store or retrieve information on
            your browser, mostly in the form of cookies. This information might
            be about you, your preferences or your device and is mostly used to
            make the site work as you expect it to. The information does not
            usually directly identify you, but it can give you a more
            personalized web experience. Because we respect your right to
            privacy, you can choose not to allow some types of cookies. Click on
            the different category headings to find out more and change our
            default settings. However, blocking some types of cookies may impact
            your experience of the site and the services we are able to offer.
          </p>

          {/* Cookie rows */}
          <div className=" rounded-[16px] ">
            <CookieRow
              number="01"
              title="Strictly Necessary Cookies"
              description="These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms. You can set your browser to block or alert you about these cookies, but some parts of the site will not then work. These cookies do not store any personally identifiable information."
              alwaysActive
              isOpen={openRow === "01"}
              onExpand={() => handleExpand("01")}
            />
            <CookieRow
              number="02"
              title="Functional Cookies"
              description="These cookies enable the website to provide enhanced functionality and personalisation. They may be set by us or by third party providers whose services we have added to our pages."
              checked={preferences.functional}
              onToggle={(v) =>
                onPreferencesChange({ ...preferences, functional: v })
              }
              isOpen={openRow === "02"}
              onExpand={() => handleExpand("02")}
            />
            <CookieRow
              number="03"
              title="Performance Cookies"
              description="These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site."
              checked={preferences.performance}
              onToggle={(v) =>
                onPreferencesChange({ ...preferences, performance: v })
              }
              isOpen={openRow === "03"}
              onExpand={() => handleExpand("03")}
            />
            <CookieRow
              number="04"
              title="Targeting Cookies"
              description="These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites."
              checked={preferences.targeting}
              onToggle={(v) =>
                onPreferencesChange({ ...preferences, targeting: v })
              }
              isOpen={openRow === "04"}
              onExpand={() => handleExpand("04")}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 mt-6">
            <button
              type="button"
              onClick={onRejectAll}
              className="w-full sm:w-auto px-4 py-2.5 rounded-full border border-[#0A0E2E] text-[#0A0E2E] text-[13px] md:text-[14px] font-medium hover:bg-[#F5F5F5] transition-colors text-center"
            >
              Reject All
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="w-full sm:w-auto px-4 py-2.5 rounded-full bg-[#F03813] text-white text-[13px] md:text-[14px] font-medium hover:bg-[#d4320f] transition-colors text-center"
            >
              Confirm my Choices
            </button>
            <button
              type="button"
              onClick={onAllowAll}
              className="w-full sm:w-auto px-4 py-2.5 rounded-full bg-[#080c2d] text-white text-[13px] md:text-[14px] font-medium flex items-center justify-center gap-2 hover:bg-[#0f1554] transition-colors group/btn sm:ml-auto"
            >
              Allow All
              <img
                src={arrowIcon}
                alt=""
                className="w-6 h-6 transition-transform duration-300 group-hover/btn:translate-x-0.5"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main CookieModal ── */
export function CookieModal({ onClose }: CookieModalProps) {
  const [showPreferences, setShowPreferences] = useState(false);

  const [preferences, setPreferences] = useState<CookiePreferences>(() => {
    try {
      const saved = localStorage.getItem("cookie-preferences");
      return saved
        ? JSON.parse(saved)
        : { functional: false, performance: true, targeting: false };
    } catch {
      return { functional: false, performance: true, targeting: false };
    }
  });

  const handleAcceptAll = useCallback(() => {
    const all = { functional: true, performance: true, targeting: true };
    setPreferences(all);
    localStorage.setItem("cookie-preferences", JSON.stringify(all));
    onClose();
  }, [onClose]);

  const handleRejectAll = useCallback(() => {
    const none = { functional: false, performance: false, targeting: false };
    setPreferences(none);
    localStorage.setItem("cookie-preferences", JSON.stringify(none));
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    localStorage.setItem("cookie-preferences", JSON.stringify(preferences));
    onClose();
  }, [onClose, preferences]);

  return createPortal(
    <>
      {!showPreferences && (
        <CookieBanner
          onManage={() => setShowPreferences(true)}
          onRejectAll={handleRejectAll}
          onAcceptAll={handleAcceptAll}
        />
      )}

      {showPreferences && (
        <CookiePreferenceModal
          preferences={preferences}
          onPreferencesChange={setPreferences}
          onRejectAll={handleRejectAll}
          onConfirm={handleConfirm}
          onAllowAll={handleAcceptAll}
          onClose={() => setShowPreferences(false)}
        />
      )}
    </>,
    document.body
  );
}

/* ── Context ── */
import { createContext, useContext } from "react";

interface CookieModalContextValue {
  showCookieBanner: () => void;
}

const CookieModalContext = createContext<CookieModalContextValue | null>(null);

export function useCookieModal() {
  const ctx = useContext(CookieModalContext);
  if (!ctx)
    throw new Error("useCookieModal must be used within CookieModalProvider");
  return ctx;
}

export function CookieModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Auto-show on first visit
  useEffect(() => {
    const accepted = localStorage.getItem("cookie-consent");
    if (!accepted) setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    localStorage.setItem("cookie-consent", "true");
    setIsOpen(false);
  }, []);

  return (
    <CookieModalContext.Provider
      value={{ showCookieBanner: () => setIsOpen(true) }}
    >
      {children}
      {isOpen && <CookieModal onClose={handleClose} />}
    </CookieModalContext.Provider>
  );
}
